import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const OVYC_LOGO = "https://customer-assets.emergentagent.com/job_b04ed63f-4684-4945-95bf-d34ff5934b34/artifacts/82m2edfj_updatred%20ovyc%20logo%20non%20transparent.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Partner With Us', path: '/partner' },
  { name: 'Impact', path: '/impact' },
  { name: 'Reports', path: '/reports' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#FAF9F6]/95 backdrop-blur-xl shadow-sm border-b border-[#E6E2D8]' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="navbar-logo"
          >
            <img 
              src={OVYC_LOGO} 
              alt="OVYC - Our Village, Your Community" 
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium transition-colors hover:text-[#E3B505] ${
                  location.pathname === link.path 
                    ? 'text-[#E3B505]' 
                    : 'text-[#5C5852]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact">
              <Button 
                data-testid="nav-contact-btn"
                className="bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full px-6 h-10 font-medium shadow-sm"
              >
                Contact Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 rounded-lg hover:bg-[#F4F1DE] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#2D2A26]" />
            ) : (
              <Menu className="w-6 h-6 text-[#2D2A26]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`lg:hidden absolute top-full left-0 w-full bg-[#FAF9F6] border-b border-[#E6E2D8] shadow-lg transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`block py-2 text-base font-medium transition-colors ${
                location.pathname === link.path 
                  ? 'text-[#E3B505]' 
                  : 'text-[#5C5852] hover:text-[#E3B505]'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/contact" className="block pt-2">
            <Button 
              data-testid="mobile-contact-btn"
              className="w-full bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-12 font-medium"
            >
              Contact Us
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
